import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { EMPTY, NEVER, Observable } from 'rxjs';
import { catchError, retry, tap } from 'rxjs/operators';

import { Toast } from '@stylehaven/components';
import { NotificationSearch } from '../../entities/notifications/notification-search.entity';
import { NotificationEntity } from '../../entities/notifications/notification.entity';
import { PaginatedResults } from '../../record/entities/pagination/paginated-results.entity';
import { RecordNavigationService } from '../../services/navigation/record-navigation.service';
import { NotificationService } from '../../services/notifications/notification.service';
import { ToastHelper } from '../../toast/service/toast.helper';

@Injectable()
export class NotificationsSandboxService {
    constructor(
        private notificationService: NotificationService,
        private navigationService: RecordNavigationService,
        private readonly translateService: TranslateService,
        private toastHelper: ToastHelper,
        private router: Router
    ) {}

    getNotifications(notificationSearch: NotificationSearch): Observable<PaginatedResults<NotificationEntity>> {
        return this.notificationService.getAll(notificationSearch);
    }

    getNewNotificationsCount(): Observable<number> {
        return this.notificationService.getCount({ onlyNew: true });
    }

    markNotificationsAsOutdated(): Observable<void> {
        const currentDate: Date = new Date();
        return this.notificationService.markNotificationsAsOutdated(currentDate).pipe(
            retry(1),
            catchError(() => EMPTY)
        );
    }

    deleteNotification(notification: NotificationEntity): Observable<void> {
        const toast: Toast = this.createStartDeleteToast();

        return this.notificationService.delete(notification.Id).pipe(
            tap(() => this.createSuccessDeleteToast(toast)),
            catchError(() => {
                this.createFailDeleteToast(toast);
                return NEVER;
            })
        );
    }

    deleteNotifications(notifications: NotificationEntity[]): Observable<void> {
        return this.notificationService.deleteBulk(notifications.map(notification => notification.Id));
    }

    navigateTo(recordId: string): void {
        this.navigationService.navigateById(recordId).subscribe();
    }

    navigateToDepotRequests(requestId: string): void {
        this.router.navigate(['/depot-requests', requestId]);
    }

    createStartDeleteToast(count?: number): Toast {
        return this.toastHelper.createStartToast('notifications.delete', count);
    }

    createSuccessDeleteToast(startToast: Toast, count?: number): void {
        this.toastHelper.updateToSuccess(startToast.id, 'notifications.delete', count);
    }

    createFailDeleteToast(startToast: Toast, count?: number): void {
        this.toastHelper.updateToFailure(startToast.id, 'notifications.delete', count);
    }
}
